import React, { useMemo, useState } from "react";
import { useBetSlip } from "../providers/BetSlipProvider";
import { useCalculateOdds } from "../hooks/useCalculateOdds";
import { useBonuses } from "../providers/BonusProvider";
import { useI18n } from "../providers/I18nProvider";
import {
  resolveWinBoostCondition,
  resolveWinBoostMessage,
  selectActiveBonuses,
} from "../shared/utils/bonus-utils";
import { BetSlipMode, WinBoostEligibilityCondition } from "../types/enums";

interface WinBoostProps {}

export const WinBoost: React.FC<WinBoostProps> = () => {
  const { betSlipState, stakes, globalStake, selectedBonus, config } =
    useBetSlip();
  const [dismissed, setDismissed] = useState(false);
  const { t } = useI18n();
  const { rawBonuses } = useBonuses();

  const { winBoosts } = useMemo(
    () =>
      selectActiveBonuses(
        rawBonuses,
        config.brand.staticBonuses,
        config.user.currencyId,
      ),
    [rawBonuses, config.brand.staticBonuses, config.user.currencyId],
  );

  const totalOdds = useCalculateOdds({
    selections: betSlipState.selections,
    betBuilderOdds: betSlipState.betBuilderState?.totalOdds || [],
    mode: betSlipState.mode,
  });

  const totalSelections = useMemo(
    () =>
      betSlipState.selections.length +
      (betSlipState.betBuilderState?.totalOdds?.length || 0),
    [betSlipState.selections, betSlipState.betBuilderState],
  );

  const totalStake = useMemo(() => {
    if (betSlipState.mode === BetSlipMode.Single) {
      return Object.values(stakes).reduce((sum, s) => sum + s, 0);
    }
    return globalStake;
  }, [betSlipState.mode, stakes, globalStake]);

  const { activeBoost, nextBoost } = useMemo(() => {
    const eligible = winBoosts.filter(
      (b) => totalSelections >= (b.originalBonus.minSelection || 0),
    );
    return {
      activeBoost: eligible.length > 0 ? eligible[eligible.length - 1] : null,
      nextBoost:
        winBoosts.find(
          (b) => totalSelections < (b.originalBonus.minSelection || 0),
        ) ?? null,
    };
  }, [winBoosts, totalSelections]);

  const condition = useMemo(
    () =>
      resolveWinBoostCondition(
        activeBoost,
        !!selectedBonus,
        totalStake,
        totalOdds,
        betSlipState.selections,
        betSlipState.betBuilderState?.totalOdds || [],
        stakes,
        betSlipState.mode as BetSlipMode,
      ),
    [activeBoost, selectedBonus, totalStake, totalOdds, betSlipState, stakes],
  );

  if (winBoosts.length === 0 || dismissed || !!selectedBonus) return null;
  if (condition === WinBoostEligibilityCondition.NoActiveBoost && !nextBoost)
    return null;

  const message = resolveWinBoostMessage(
    condition,
    activeBoost,
    nextBoost,
    totalSelections,
    t,
  );
  if (!message) return null;

  return (
    <div className="bg-teaser-bg text-teaser-freebet-text px-3 py-2 my-1">
      <div className="flex items-start justify-between gap-2">
        <div className="flex-1 text-sm">{message}</div>
        <button
          onClick={() => setDismissed(true)}
          className="flex-shrink-0 mt-0.5"
          aria-label="Dismiss"
        >
          <i className="au-m-ico-c au-i-cancel bg-freebet-teaser-icon-close"></i>
        </button>
      </div>
    </div>
  );
};

export default WinBoost;
